import React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'helpers/dayjs';
import {
  MdLogout, MdComputer, MdPhoneIphone, MdTablet, MdPublic, MdLan, MdPlace,
  MdWarningAmber, MdHelpOutline, MdArrowUpward, MdArrowDownward,
} from 'react-icons/md';
import { Card, Badge, Pagination, EmptyState } from 'components/common';

const DEVICE_ICONS = { mobile: MdPhoneIphone, tablet: MdTablet, desktop: MdComputer };

const RISK_STYLES = {
  high: 'tw-bg-red-50 tw-text-red-700 tw-border-red-200',
  medium: 'tw-bg-amber-50 tw-text-amber-700 tw-border-amber-200',
  low: 'tw-bg-slate-50 tw-text-slate-500 tw-border-slate-200',
};

// The reasons are the whole value of a risk score — a bare number tells nobody
// what to check. They go in the title so the detail is one hover away without
// widening the row.
const RiskChip = ({ session }) => {
  if (!session.risk_score) return null;

  const reasons = (session.risk_reasons || [])
    .map((r) => `• ${r.detail} (+${r.score})`)
    .join('\n');

  return (
    <span
      title={reasons || 'No detail recorded'}
      className={`tw-inline-flex tw-items-center tw-gap-1 tw-px-1.5 tw-py-0.5 tw-rounded-sm tw-border tw-border-solid tw-text-[10px] tw-font-bold tw-uppercase tw-cursor-help ${RISK_STYLES[session.risk_severity] || RISK_STYLES.low}`}
    >
      <MdWarningAmber size={11} /> {session.risk_severity} {session.risk_score}
    </span>
  );
};

RiskChip.propTypes = { session: PropTypes.object.isRequired };

/**
 * The session list.
 *
 * A purpose-built table rather than `common/Table`: that one takes rows as arrays
 * of cells, which leaves no place to hang a per-column renderer — and every
 * column here needs one (online dot, device icon, IP with a map affordance,
 * relative time, revoke button).
 */
// Only the columns the API will actually order by. Offering a header that
// silently does nothing is worse than a plain header.
const COLUMNS = [
  { key: 'user', label: 'User', sortable: true },
  { key: 'browser', label: 'Device / Browser', sortable: true },
  { key: 'ip_address', label: 'IP address', sortable: true },
  { key: null, label: 'Location', sortable: false },
  { key: null, label: 'Signed in via', sortable: false },
  { key: 'last_seen_at', label: 'Last seen', sortable: true },
];

const SortableHeader = ({ column, sortBy, sortDir, onSort }) => {
  if (!column.sortable) {
    return <th className="tw-px-3 tw-py-2.5 tw-font-semibold">{column.label}</th>;
  }

  const active = sortBy === column.key;

  return (
    <th className="tw-px-3 tw-py-2.5 tw-font-semibold">
      <button
        type="button"
        onClick={() => onSort(column.key)}
        className={`tw-inline-flex tw-items-center tw-gap-1 tw-bg-transparent tw-border-0 tw-p-0 tw-uppercase tw-tracking-wider tw-text-xs tw-font-semibold ${active ? 'tw-text-primary-600' : 'tw-text-slate-400 hover:tw-text-slate-600'}`}
      >
        {column.label}
        {active && (sortDir === 'asc' ? <MdArrowUpward size={13} /> : <MdArrowDownward size={13} />)}
      </button>
    </th>
  );
};

SortableHeader.propTypes = {
  column: PropTypes.object.isRequired,
  sortBy: PropTypes.string,
  sortDir: PropTypes.string,
  onSort: PropTypes.func.isRequired,
};

const SessionTable = ({
  rows, totalRows, page, perPage, loading, sortBy, sortDir, onSort,
  onPageChange, onRevoke, onRevokeAll, onShowLocation,
}) => {
  if (!loading && rows.length === 0) {
    return (
      <Card padding="none">
        <EmptyState
          icon={MdComputer}
          title="No sessions"
          description="Nobody is signed in under the current filter."
        />
      </Card>
    );
  }

  return (
    <Card padding="none">
      <div>
        {/* table-fixed, not the browser default.
            Under auto layout a column grows to fit its longest cell, so
            `truncate` on a name like "Thao - TLD - PEB Steel HCMC - Marketing"
            did nothing: there was no width to truncate against, the column
            widened, the table outgrew the card, and a horizontal scrollbar
            appeared across a page that visibly had room to spare.

            No min-width and no overflow container either — an earlier version
            kept a 1060px floor "for narrow screens", and on any viewport under
            it macOS Safari (with scroll bars set to always show) parked a
            permanent grey scrollbar under all three tabs. Fixed layout means
            the table can compress instead: every cell truncates against its
            column, so the table always fits and there is nothing to scroll. */}
        <table className="tw-w-full tw-table-fixed tw-text-sm tw-border-collapse">
          {/* Widths in % so the columns shrink with the viewport; User takes
              the remainder and absorbs wide screens. */}
          <colgroup>
            <col />
            <col style={{ width: '16%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '12%' }} />
            <col style={{ width: '104px' }} />
          </colgroup>
          <thead className="tw-bg-slate-50">
            <tr className="tw-text-left tw-text-xs tw-uppercase tw-tracking-wider tw-text-slate-400">
              {COLUMNS.map((column) => (
                <SortableHeader
                  key={column.label}
                  column={column}
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={onSort}
                />
              ))}
              <th className="tw-px-3 tw-py-2.5" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const DeviceIcon = DEVICE_ICONS[row.device_type] || MdComputer;

              return (
                <tr key={row.id} className="tw-border-b tw-border-solid tw-border-slate-100 hover:tw-bg-slate-50">
                  <td className="tw-px-4 tw-py-2.5">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      {/* Green only for a live heartbeat. An open session that has
                          been idle for an hour is not the same as someone at
                          their desk, and colouring both green would hide that. */}
                      <span
                        className={`tw-w-2 tw-h-2 tw-rounded-xl tw-shrink-0 ${row.online ? 'tw-bg-green-500' : 'tw-bg-slate-300'}`}
                        title={row.online ? 'Active now' : 'Idle'}
                      />
                      <div className="tw-min-w-0">
                        <p className="tw-m-0 tw-font-medium tw-text-slate-800 tw-truncate tw-flex tw-items-center tw-gap-1.5">
                          {row.user?.full_name || row.user?.name || '—'}
                          <RiskChip session={row} />
                        </p>
                        <p className="tw-m-0 tw-text-xs tw-text-slate-400 tw-truncate">{row.user?.email}</p>
                      </div>
                    </div>
                  </td>

                  <td className="tw-px-3 tw-py-2.5">
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <DeviceIcon size={16} className="tw-text-slate-400 tw-shrink-0" />
                      {/* min-w-0 is what lets the truncation below engage; a
                          flex child defaults to min-content and would push the
                          fixed column open instead. */}
                      <div className="tw-min-w-0">
                        <p className="tw-m-0 tw-text-slate-700 tw-truncate">{row.device_label || 'Unknown'}</p>
                        <p className="tw-m-0 tw-text-xs tw-text-slate-400 tw-truncate">{row.os || '—'}</p>
                      </div>
                    </div>
                  </td>

                  <td className="tw-px-3 tw-py-2.5">
                    <div className="tw-flex tw-items-center tw-gap-1.5">
                      {row.is_private_ip
                        ? <MdLan size={14} className="tw-text-slate-400 tw-shrink-0" />
                        : <MdPublic size={14} className="tw-text-slate-400 tw-shrink-0" />}
                      <span className="tw-font-mono tw-text-xs tw-text-slate-700">{row.ip_address || '—'}</span>
                    </div>
                  </td>

                  {/* Location gets its own column rather than hiding under the IP:
                      tucked away, nobody found the map at all. */}
                  <td className="tw-px-3 tw-py-2.5">
                    {row.mappable ? (
                      <button
                        type="button"
                        onClick={() => onShowLocation(row)}
                        title="Show the approximate location of this IP on a map"
                        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-max-w-full tw-px-2 tw-h-7 tw-rounded-md tw-border tw-border-solid tw-border-primary-200 tw-bg-primary-50 tw-text-xs tw-font-semibold tw-text-primary-700 hover:tw-bg-primary-100 tw-transition-colors"
                      >
                        <MdPlace size={13} className="tw-shrink-0" />
                        <span className="tw-truncate">{row.location || 'View on map'}</span>
                      </button>
                    ) : (
                      // Says WHY there is no map instead of leaving a blank cell.
                      // A LAN address genuinely cannot be geolocated, and without
                      // this the missing button reads as a broken feature.
                      <span
                        className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-slate-400 tw-cursor-help"
                        title={row.is_private_ip
                          ? 'Signed in from inside the company network. Private addresses (192.168.x, 10.x) are not routable on the internet, so there is no location to look up.'
                          : 'No location on record for this address — either the geolocation database is not installed on the server, or it has no entry for this IP.'}
                      >
                        <MdHelpOutline size={12} />
                        {row.location || 'Not locatable'}
                      </span>
                    )}
                  </td>

                  <td className="tw-px-3 tw-py-2.5">
                    {/* provider_label, not the raw column: the filter above
                        offers "Google" while the cell said `google_oauth2`, so
                        the two did not look like the same thing. */}
                    <Badge variant="default" size="sm">
                      {row.provider_label || row.sign_in_provider || 'Password'}
                    </Badge>
                  </td>

                  {/* truncate rather than nowrap: with percentage columns a
                      narrow viewport must shorten this cell, not overflow it. */}
                  <td className="tw-px-3 tw-py-2.5 tw-text-slate-600 tw-truncate">
                    {row.last_seen_at ? dayjs(row.last_seen_at).fromNow() : '—'}
                    {row.ended_at && (
                      <p className="tw-m-0 tw-text-xs tw-text-slate-400">
                        Ended {dayjs(row.ended_at).fromNow()} · {row.ended_reason}
                      </p>
                    )}
                  </td>

                  <td className="tw-px-3 tw-py-2.5 tw-text-right">
                    {row.active && (
                      <div className="tw-inline-flex tw-gap-1">
                        <button
                          type="button"
                          onClick={() => onRevoke(row)}
                          title="Sign this device out"
                          className="tw-inline-flex tw-items-center tw-justify-center tw-h-8 tw-w-8 tw-rounded-md tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-text-slate-500 hover:tw-bg-red-50 hover:tw-text-red-600 hover:tw-border-red-200 tw-transition-colors"
                        >
                          <MdLogout size={15} />
                        </button>
                        {/* The response to a compromised account, as opposed to
                            one lost laptop. Kept separate and labelled so the
                            two are never confused under time pressure. */}
                        <button
                          type="button"
                          onClick={() => onRevokeAll(row)}
                          title={`Sign out ALL devices for ${row.user?.name || 'this user'}`}
                          className="tw-inline-flex tw-items-center tw-justify-center tw-h-8 tw-px-2 tw-rounded-md tw-border tw-border-solid tw-border-slate-200 tw-bg-white tw-text-[11px] tw-font-semibold tw-text-slate-500 hover:tw-bg-red-50 hover:tw-text-red-600 hover:tw-border-red-200 tw-transition-colors"
                        >
                          All
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* totalRows comes from the server, so this is the true page count — the
          fetched rows are only ever one page long. */}
      {totalRows > perPage && (
        <div className="tw-px-4 tw-py-3 tw-border-t tw-border-solid tw-border-slate-100">
          <Pagination
            currentPage={page}
            totalPages={Math.ceil(totalRows / perPage)}
            totalResults={totalRows}
            perPage={perPage}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </Card>
  );
};

SessionTable.propTypes = {
  rows: PropTypes.array.isRequired,
  totalRows: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  perPage: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  sortBy: PropTypes.string,
  sortDir: PropTypes.string,
  onSort: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRevoke: PropTypes.func.isRequired,
  onRevokeAll: PropTypes.func.isRequired,
  onShowLocation: PropTypes.func.isRequired,
};

export default SessionTable;
